import { useModal } from "@surveying-hub-bv/fe-component-library";
import { Currency, currencyToSymbolMapper } from "../utils/currencyToSymbolMapper"
import { DecimalPrice } from "../utils/DecimalPrice"
import { Button } from "./Buttons"
import { GenericPaymentDetailsErrorDialog } from "../pages/checkout/dialogs/ErrorDialogs";
import { Trans, useTranslation } from "react-i18next";
import EagerSVGIcon from "./EagerSVG";
import { useState } from "react";


const getGridColsClass = (numProducts: number) => {
    if (numProducts === 1) return "grid-cols-1"
    if (numProducts === 2) return "md:grid-cols-2"
    if (numProducts === 3) return "md:grid-cols-2 lg:grid-cols-3"
    return "md:grid-cols-2 lg:grid-cols-4"
};

export const PricingTableCardsWrapper = ({ children, productLength, fullWidth }: { children: React.ReactNode, productLength: number, fullWidth?: boolean }) => {
    const cols = getGridColsClass(productLength)

    return (
        <div className={`grid grid-cols-1 ${cols} items-center gap-x-4 gap-y-6 w-full ${fullWidth ? "max-w-full" : "max-w-[1000px]"} mx-auto`}>
            {children}
        </div>
    )
}


type PricingTableCardBase = {
    name: string
    priceId: string
    createCheckoutSession: (priceId: string) => Promise<string>
    amount: number
    currency: Currency
    bestChoice?: boolean // Set to true to add 'Recommended' or 'Most popular' label. Only ONE card can have this label.
    attributes?: string[]
    testMode?: boolean // Set to true to add 'Test Mode' label.
    previewMode?: boolean // makes buttons unclickable. Meant for the preview dialog.
    customClassName?: string // Custom class name for the card
}

type PricingTableCardSubscription = PricingTableCardBase & {
    type: 'Subscription'
    interval: 'YEARLY' | 'MONTHLY' | 'WEEKLY'
}
type PricingTableCardCredit = PricingTableCardBase & {
    type: 'Token'
    creditsAmount: number
}

export type PricingTableCardProps = PricingTableCardSubscription | PricingTableCardCredit



export const PricingTableCard = ({ name, priceId, createCheckoutSession, type, amount, currency, bestChoice, attributes, testMode, previewMode, customClassName, ...rest }: PricingTableCardProps) => {
    const { showModal, hideModal } = useModal()
    const { t } = useTranslation()
    const [isSending, setIsSending] = useState(false);

    const interval = type === "Subscription" ? (rest as PricingTableCardSubscription).interval : undefined;

    const intervalMapper = {
        YEARLY: <Trans
            i18nKey="pricing.perYear"
            components={{
                br: <br />
            }}
        />,
        MONTHLY: <Trans
            i18nKey="pricing.perMonth"
            components={{
                br: <br />
            }}
        />,
        WEEKLY: <Trans
            i18nKey="pricing.perWeek"
            components={{
                br: <br />
            }}
        />,
    }

    const createCheckoutSessionHandler = async (priceId: string) => {
        setIsSending(true)
        const sessionId = await createCheckoutSession(priceId)

        const newTab = window.open('', '_blank');

        try {
            const url = sessionId;
            if (newTab) {
                newTab.location.href = url; // Update the new tab with the actual URL
            } else {
                window.location.href = url; // Fallback if window.open was blocked
            }
        } catch (error: any) {
            if (error.response?.status === 409) {
                showModal(<GenericPaymentDetailsErrorDialog />);
            } else {
                showModal(<GenericPaymentDetailsErrorDialog />);
            }
        }

        setIsSending(false)
        hideModal()
    }

    const price = `${currencyToSymbolMapper[currency]} ${new DecimalPrice(amount, currency).value}`

    return (
        <div className={`flex flex-col md:max-w-[400px]mx-auto md:mx-0 h-full pt-8 p-6 border ${bestChoice ? "dark:bg-darkMode-layer3color dark:border-darkMode-primary" : " dark:bg-darkMode-layer2color dark:border-darkMode-layer2border"} relative rounded-lg ${customClassName}`}>
            <h4 className="text-[20px] break-words mb-2">{name}{testMode ? <span className=" ml-2 text-[12px] font-bold dark:bg-darkMode-warning dark:text-darkMode-warningDark px-1.5 rounded whitespace-nowrap uppercase">{t("pricing.testMode")}</span> : ""}</h4>

            {/* Recommended Badge */}
            {bestChoice && (
                <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2 dark:bg-darkMode-primary text-black text-sm font-bold px-2 py-1 rounded-md">
                    {t("pricing.recommended")}
                </div>
            )}

            {/* Credits Amount */}
            {type === "Token" && (
                <p className="dark:text-darkMode-secondaryText mt-2 mb-4">
                    Credits: <span className="dark:text-white font-bold">{(rest as PricingTableCardCredit).creditsAmount}</span>
                </p>
            )}

            {/* Description --- not being added yet */}
            {/* <span className="dark:text-darkMode-secondaryText text-[12px] font-normal"></span> */}

            {/* Price */}
            <p className="flex items-center gap-1.5 text-[32px] font-bold mt-6 mb-2">
                {price}
                {type === "Subscription" && interval && <span className="dark:text-darkMode-secondaryText text-[12px] font-normal">{intervalMapper[interval]}</span>}
            </p>

            {previewMode === true ?
                <Button
                    label={type === "Subscription"
                        ? t("pricing.subscribe")
                        : t("pricing.purchase")
                    }
                    fauxButton
                    alwaysFullWidth
                    size="small" />
                :
                <Button
                    label={isSending ?
                        t("common.loading")
                        :
                        type === "Subscription"
                            ? t("pricing.subscribe")
                            : t("pricing.purchase")
                    }
                    onClick={() => createCheckoutSessionHandler(priceId)}
                    alwaysFullWidth
                    size="small" />
            }

            {attributes !== undefined && attributes.length > 0 &&
                <>
                    <p className="mt-4 mb-2 text-[14px]">{t("pricing.thisIncludes")}</p>
                    <ul>
                        {attributes.map((attribute, index) => (
                            <li key={index} className="font-normal text-[14px] flex items-center gap-3 mb-2 last:mb-0 dark:text-darkMode-secondaryText"><EagerSVGIcon name="SuccessIcon" width={20} height={20} className="flex-shrink-0" />{attribute}</li>
                        ))}
                    </ul>
                </>
            }
        </div>
    )
}
